import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import { fitzRectToCssOverlay } from '../utils/auditCoordinates';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// react-pdf resets workerSrc to "pdf.worker.mjs" on import — set it here in the
// same module that renders <Document>, using a CDN URL that matches pdfjs.version.
// (CRA often serves public/*.mjs with the wrong MIME type, which breaks module workers.)
if (typeof Promise.withResolvers !== 'function') {
  Promise.withResolvers = function withResolvers() {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Default zoom = 1.0 means "fit page to container width".
// Values >1.0 zoom in (page wider than container → horizontal scroll).
// Values <1.0 zoom out (page narrower than container → centred).
const DEFAULT_SCALE = 1.0;
const MIN_SCALE = 0.5;
const MAX_SCALE = 2.0;

// How long (ms) to wait after the last file change before handing a new file
// to <Document>.  This coalesces rapid navigation bursts so react-pdf never
// receives two file-changes faster than it can clean up, which prevents the
// "PDFWorker.fromPort — worker is being destroyed" crash.
const FILE_DEBOUNCE_MS = 200;

function HighlightLayer({ pageProxy, scale, highlight, pageNumber }) {
  const style = useMemo(() => {
    if (!highlight || highlight.page !== pageNumber || !pageProxy) return null;
    return fitzRectToCssOverlay(
      { x1: highlight.x1, y1: highlight.y1, x2: highlight.x2, y2: highlight.y2 },
      pageProxy,
      scale
    );
  }, [highlight, pageNumber, pageProxy, scale]);

  if (!style) return null;

  return (
    <Box
      className="audit-highlight-active"
      sx={{
        position: 'absolute',
        left: style.left,
        top: style.top,
        width: style.width,
        height: style.height,
        pointerEvents: 'none',
        border: '2px solid rgba(255, 152, 0, 0.95)',
        bgcolor: 'rgba(255, 152, 0, 0.2)',
        boxSizing: 'border-box',
        borderRadius: '2px',
        zIndex: 2
      }}
    />
  );
}

/**
 * Renders a single PDF page at a given pixel width.
 *
 * Using `width` instead of `scale` on react-pdf's <Page> means every vendor's
 * PDF — regardless of its physical page size — renders at exactly the width we
 * specify.  This prevents FreePeople (and any other vendor whose PDFs have
 * non-standard page dimensions) from appearing "zoomed in".
 *
 * HighlightLayer still needs the *actual* scale factor (= renderWidth / page's
 * natural width in points) to convert PyMuPDF coordinates to CSS pixels via
 * fitzRectToCssOverlay.  We derive it from pageProxy.originalWidth after load.
 */
function AuditPageFrame({ pageNumber, renderWidth, highlight }) {
  const [pageProxy, setPageProxy] = useState(null);
  const [actualScale, setActualScale] = useState(1);

  const onLoadSuccess = useCallback((page) => {
    setPageProxy(page);
    if (page.originalWidth > 0 && renderWidth > 0) {
      setActualScale(renderWidth / page.originalWidth);
    }
  }, [renderWidth]);

  // Re-derive actual scale whenever the rendered width changes (zoom controls
  // or browser window resize).
  useEffect(() => {
    if (pageProxy?.originalWidth > 0 && renderWidth > 0) {
      setActualScale(renderWidth / pageProxy.originalWidth);
    }
  }, [renderWidth, pageProxy]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        minWidth: 'max-content',
        mb: 1.5,
        '& .react-pdf__Page': { boxShadow: '0 1px 6px rgba(0,0,0,0.35)' }
      }}
    >
      <Page
        pageNumber={pageNumber}
        width={renderWidth || undefined}
        onLoadSuccess={onLoadSuccess}
        renderAnnotationLayer={false}
        renderTextLayer
      >
        {/* Pass actualScale (derived from rendered width) not the zoom slider value */}
        <HighlightLayer
          pageProxy={pageProxy}
          scale={actualScale}
          highlight={highlight}
          pageNumber={pageNumber}
        />
      </Page>
    </Box>
  );
}

const AuditPdfViewer = ({ file, externalError, highlight, documentKey }) => {
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  // containerWidth is measured via ResizeObserver so the page always fills the
  // viewer regardless of PDF page dimensions.
  const [containerWidth, setContainerWidth] = useState(600);
  const scrollRootRef = useRef(null);
  const lastScrolledPageRef = useRef(null);
  const deferTimerRef = useRef(null);

  // Observe the scroll container so we always know how wide the viewable area
  // is.  clientWidth gives the initial value synchronously to avoid a first-
  // render flash.
  useEffect(() => {
    const el = scrollRootRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth || 600);
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Pixel width to hand to react-pdf's <Page>.
  // At scale=1.0 (default) the page fills the container minus padding (16 px).
  // At scale>1.0 the page is wider → horizontal scrollbar appears (zoomed in).
  // At scale<1.0 the page is narrower → centred with side margins.
  const pageRenderWidth = Math.max(200, (containerWidth - 16) * scale);

  // Build a react-pdf file source. Prefer string/Blob URLs over raw TypedArrays
  // — CRA + pdf.js is more reliable with URLs.
  const blobUrlRef = useRef(null);
  const fileSource = useMemo(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    if (!file) return null;
    if (typeof file === 'string' && file.trim()) return file.trim();
    if (typeof file?.url === 'string' && file.url.trim()) return file.url.trim();
    if (file?.data?.byteLength) {
      const url = URL.createObjectURL(new Blob([file.data], { type: 'application/pdf' }));
      blobUrlRef.current = url;
      return url;
    }
    return null;
  }, [file]);

  // Debounced source — this is what <Document> actually receives.
  //
  // Why debounce? When the user clicks Accept/Reject rapidly, each click
  // navigates to the next PO (URL param change) and AuditPage calls
  // setPdfFile() for the new PO, often hitting the prefetch cache
  // synchronously.  That causes `fileSource` to change multiple times in a
  // single render cycle.  react-pdf responds by cancelling the in-progress
  // load task and starting a fresh one on the same worker — if the previous
  // task hasn't finished initialising its worker port yet, the library throws:
  //   "PDFWorker.fromPort — the worker is being destroyed."
  //
  // By debouncing we ensure <Document> only ever sees one file change per
  // burst of navigation, so there is always a clean 200 ms gap between
  // task teardown and the next task start.
  const [deferredFileSource, setDeferredFileSource] = useState(fileSource);
  const [viewerError, setViewerError] = useState('');

  useEffect(() => {
    clearTimeout(deferTimerRef.current);

    if (!fileSource) {
      // Null means no PDF — propagate immediately so we show the right message.
      setDeferredFileSource(null);
      return;
    }

    deferTimerRef.current = setTimeout(
      () => setDeferredFileSource(fileSource),
      FILE_DEBOUNCE_MS
    );

    return () => clearTimeout(deferTimerRef.current);
  }, [fileSource]);

  // Collapse the page list immediately when the PO changes so stale pages
  // from the previous document don't flash while we wait for the debounce.
  useEffect(() => {
    setNumPages(0);
    setScale(DEFAULT_SCALE);
    setViewerError('');
    lastScrolledPageRef.current = null;
  }, [documentKey, deferredFileSource]);

  useEffect(() => () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);
  // Scroll to the highlighted page whenever the highlight target changes.
  useEffect(() => {
    if (!highlight?.page || !scrollRootRef.current) return;
    if (lastScrolledPageRef.current === highlight.page) return;
    const el = scrollRootRef.current.querySelector(`[data-page-number="${highlight.page}"]`);
    el?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    lastScrolledPageRef.current = highlight.page;
  }, [highlight]);

  // Fine-tune: make sure the highlight box itself is fully visible.
  useEffect(() => {
    if (!highlight || !scrollRootRef.current) return;
    const root = scrollRootRef.current;
    const isFullyVisible = (container, element) => {
      const c = container.getBoundingClientRect();
      const e = element.getBoundingClientRect();
      return e.top >= c.top && e.bottom <= c.bottom;
    };
    const raf = requestAnimationFrame(() => {
      const box = root.querySelector('.audit-highlight-active');
      if (!box) return;
      if (!isFullyVisible(root, box)) {
        box.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [highlight]);

  if (externalError) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={280} px={2}>
        <Typography color="error" textAlign="center">
          {externalError}
        </Typography>
      </Box>
    );
  }

  // Show a placeholder while waiting for the first file or the debounce to settle.
  if (!deferredFileSource) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={320}>
        <Typography color="text.secondary">
          {fileSource ? 'Loading PDF…' : 'No PDF loaded'}
        </Typography>
      </Box>
    );
  }

  // If pdf.js fails, fall back to the browser's native PDF viewer (iframe).
  // Highlights are unavailable in this mode, but the document is still readable.
  if (viewerError) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          bgcolor: '#1a1d26',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography color="warning.main" sx={{ px: 1.5, py: 1, fontSize: '0.75rem' }}>
          Advanced viewer unavailable — showing browser PDF preview.
        </Typography>
        <iframe
          src={`${deferredFileSource}#toolbar=1`}
          title="PDF Document"
          style={{ flex: 1, width: '100%', border: 'none', minHeight: 320 }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRootRef}
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        bgcolor: '#1a1d26',
        borderRadius: 1,
        overflow: 'auto',
        p: 1
      }}
    >
      {/* Zoom controls — percentage is relative to "fit width" (100% = fills pane) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5,
          p: 0.5,
          bgcolor: 'rgba(26,29,38,0.8)',
          backdropFilter: 'blur(2px)'
        }}
      >
        <IconButton
          size="small"
          onClick={() => setScale((s) => Math.max(MIN_SCALE, +(s - 0.1).toFixed(2)))}
          sx={{ color: '#fff' }}
          title="Zoom out"
        >
          <Remove fontSize="small" />
        </IconButton>
        <Typography variant="caption" sx={{ color: '#fff', minWidth: 42, textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </Typography>
        <IconButton
          size="small"
          onClick={() => setScale((s) => Math.min(MAX_SCALE, +(s + 0.1).toFixed(2)))}
          sx={{ color: '#fff' }}
          title="Zoom in"
        >
          <Add fontSize="small" />
        </IconButton>
      </Box>

      {/*
        No `key` prop on <Document> — changing the `file` prop reuses the same
        PDF.js worker instead of tearing it down and recreating it (which is the
        other half of preventing the worker-destruction crash).
      */}
      <Document
        file={deferredFileSource}
        onLoadSuccess={({ numPages: n }) => {
          setViewerError('');
          setNumPages(n);
        }}
        onLoadError={(err) => {
          console.error('PDF load error:', err);
          setViewerError(err?.message || String(err) || 'Could not open this PDF in the viewer.');
        }}
        loading={
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">Loading PDF…</Typography>
          </Box>
        }
        error={
          <Box py={4} px={2} textAlign="center">
            <Typography color="error">Could not open this PDF in the viewer.</Typography>
            {viewerError ? (
              <Typography color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                {viewerError}
              </Typography>
            ) : null}
          </Box>
        }
      >
        {numPages > 0 &&
          Array.from({ length: numPages }, (_, i) => {
            const pageNumber = i + 1;
            return (
              <AuditPageFrame
                key={pageNumber}
                pageNumber={pageNumber}
                renderWidth={pageRenderWidth}
                highlight={highlight}
              />
            );
          })}
      </Document>
    </Box>
  );
};

export default AuditPdfViewer;
